<template>
  <div v-if="sample_list" class="animated fadeIn">
    <template v-if="sample_list.length">
      <b-table
        :items="sample_list"
        :fields="table.fields"
        :per-page="table.per_page"
        v-sortable="table.sorts"
        :current-page="table.current_page"
        id="schedule-instance-samples"
        responsive
      >
        <template #cell(order)="data">
          {{ getSampleOrder(data.item) }}
        </template>
        <template #cell(jig)="data">
          {{ getJigName(data.item) }}
        </template>
        <template #cell(pid)="data">
          <existence-gate
            :entity="data.item"
            :levels="['sample']"
            :field="'pid'"
          >
            <template #existing-content>
              <router-link
                :to="`/samples/${getData(data.item, {
                  levels: ['sample'],
                  field: 'id',
                })}`"
              >
                {{ data.item.sample.pid }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(receiving_id)="data">
          <existence-gate
            :entity="data.item"
            :levels="['sample']"
            :field="'receiving_id'"
          >
            <template #existing-content>
              <template v-if="data.item.sample.receiving_id">
                {{ data.item.sample.receiving_id }}
              </template>
              <a v-else class="btn btn-secondary disabled px-1 py-0">
                Not Received
              </a>
            </template>
          </existence-gate>
        </template>
        <template #cell(customer)="data">
          <existence-gate
            :entity="data.item"
            :levels="table_data.customer"
            :field="'name'"
          >
            <template #existing-content>
              <router-link
                :to="`/customer/${getData(data.item, {
                  levels: table_data.customer,
                  field: 'id',
                })}`"
              >
                {{
                  getData(data.item, {
                    levels: table_data.customer,
                    field: "full_name",
                  })
                }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(submission_pid)="data">
          <existence-gate
            :entity="data.item"
            :levels="table_data.submission"
            :field="'pid'"
          >
            <template #existing-content>
              <router-link
                :to="`/samples/${getData(data.item, {
                  levels: table_data.submission,
                  field: 'id',
                })}`"
              >
                {{
                  data.item.sample.submission.pid
                }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(identifier)="data">
          <existence-gate
            :entity="data.item"
            :levels="['sample']"
            :field="'identifier'"
          >
            <template #existing-content>
              {{
                getData(data.item, {
                  levels: ["sample"],
                  field: "identifier",
                })
              }}
            </template>
          </existence-gate>
        </template>
        <template #cell(number)="data">
          <existence-gate
            :entity="data.item"
            :levels="['sample']"
            :field="'number'"
          >
            <template #existing-content>
              {{
                getData(data.item, {
                  levels: ["sample"],
                  field: "number",
                })
              }}
            </template>
          </existence-gate>
        </template>
        <template #cell(created_at)="data">
          <existence-gate
            :entity="data.item"
            :levels="['sample']"
            :field="'created_at'"
          >
            <template #existing-content>
              {{ data.item.sample.created_at | moment("L") }}
            </template>
          </existence-gate>
        </template>
        <template #cell(turnaround)="data">
          <existence-gate
            :entity="data.item"
            :levels="table_data.turnaround"
            :field="'pid'"
          >
            <template #existing-content>
              <router-link
                :to="`/samples/${getData(data.item, {
                  levels: table_data.turnaround,
                  field: 'id',
                })}`"
              >
                {{
                  getData(data.item, {
                    levels: table_data.turnaround,
                    field: "name",
                  })
                }}
              </router-link>
            </template>
          </existence-gate>
        </template>
      </b-table>
      <custom-table-pagination
        :options="table.per_page_options"
        :current_page="table.current_page"
        :per_page="table.per_page"
        :total_rows="sample_list.length"
        @update="updatePaginationData"
        table_id="schedule-instance-samples"
        selector_prefix="Samples per page"
      />
    </template>
    <div v-else>
      <b-alert show class="mb-4"
        >No samples were selected for this instrument instance.</b-alert
      >
    </div>
  </div>
</template>
<script>

import sortable from "@/components/shared/directives/Sortable";
import CustomTablePagination from "@/components/shared/CustomTablePagination";
import ExistenceGate from "@/components/shared/ExistenceGate";
import { getEntityData, generatePid } from "@/shared/utils";

export default {
  name: "ScheduleDetailSampleTable",
  components: { CustomTablePagination, ExistenceGate },
  directives: { sortable },
  props: {
    sample_list: {
      type: Array,
      default: () => null,
    },
    schedule_process: {
      type: Object,
      default: () => null,
    },
    instrument_jig_collection: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      table: {
        fields: [
          { key: "order", label: "Order" },
          //{ key: "jig", label: "Jig" },
          { key: "pid", label: "Sample" },
          { key: "receiving_id", label: "RFID" },
          { key: "customer", label: "Customer" },
          { key: "submission_pid", label: "Submission" },
          { key: "identifier", label: "ID" },
          { key: "number", label: "Num." },
          { key: "created_at", label: "Submitted" },
          { key: "turnaround", label: "Turnaround" },
        ],
        sorts: null,
        per_page: 500,
        current_page: 1,
        per_page_options: [500, 750, 1000, 1250],
      },
      table_data: {
        customer: ["sample", "submission", "project", "customer"],
        submission: ["sample", "submission"],
        turnaround: ["sample", "submission", "turnaround"],
      },
    };
  },
  computed: {
    samples_on_page() {
      if (!this.sample_list) {
        return null;
      }

      if (!this.sample_list.length) {
        return 0;
      }

      if (
        this.table.per_page * this.table.current_page <=
        this.sample_list.length
      ) {
        return this.table.per_page;
      } else {
        return (
          this.sample_list.length -
          this.table.per_page * (this.table.current_page - 1)
        );
      }
    },
  },
  methods: {
    generatePid({ entity, prefix }) {
      return generatePid({ entity, prefix });
    },
    getData(entity, data) {
      return getEntityData({
        entity,
        ...data,
      });
    },
    updatePaginationData({ field, value }) {
      this.table[field] = value;
    },
    getSampleOrder(item) {
      //console.log(item);

      return item.schedule_index;
      /*       return this.schedule_process.process_schedule_samples.find(
        ({ sample_id, process_task_id }) =>
          sample_id === sample.sample_id &&
          process_task_id === sample.schedulable_task_id
      ).order; */
    },
    getJigName({ instrument_jig_id }) {
      const jig = this.instrument_jig_collection.models.find(
        ({ id }) => id === instrument_jig_id
      );
      return jig ? jig.jig.name : null;
    },
  },
};
</script>
